"use client";
import React, { useState, useEffect } from "react";
import { searchLocations } from "../../app/utilities/apis";
import CustomInput from "./CustomInput";
import ImageWrapper from "./ImageWrapper";
import { MODAL_TRACKING } from "@/lib/constants/tracking";

export interface LocationSuggestion {
    // suggestions may contain rich location objects from the API — keep flexible
    full_text_search?: string;
    location_id?: number;
    [key: string]: any;
}

interface LocationMasterSearchInputProps {
    value: string;
    onChange: (value: string, selectedLocation: LocationSuggestion | null) => void;
    error?: string;
    placeholder?: string;
    className?: string;
    trackingClassName?:string;
}

const LocationMasterSearchInput: React.FC<LocationMasterSearchInputProps> = ({
    value,
    onChange,
    error,
    placeholder = "Enter your City or Pincode",
    className = "",
    trackingClassName = "",
}) => {
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentKeyword, setCurrentKeyword] = useState("");

    useEffect(() => {
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [debounceTimer]);

    useEffect(() => {
        if (!showLocationDropdown) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".location-search-wrapper")) {
                // If user clicks outside without selecting, clear the input
                if (!selectedLocation) {
                    onChange("", null);
                }
                setShowLocationDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showLocationDropdown, selectedLocation, onChange]);

    // Clear input and suggestions when clear icon is clicked
    const clearInput = () => {
        onChange("", null);
        setSelectedLocation(null);
        setLocationSuggestions([]);
        setShowLocationDropdown(false);
        setCurrentPage(1);
        setTotalPages(1);
        setCurrentKeyword("");
    };

    // Fetch location suggestions from API
    const fetchLocationSuggestionsAPI = async (keyword: string, page: number = 1) => {
        if (!keyword.trim()) {
            setLocationSuggestions([]);
            setShowLocationDropdown(false);
            setCurrentPage(1);
            setTotalPages(1);
            return;
        }

        if (page === 1) {
            setIsLoadingLocations(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const data = await searchLocations({ q: keyword, page, lang: "english" });
            
            if (data.items?.length) {
                if (page === 1) {
                    setLocationSuggestions(data.items);
                } else {
                    setLocationSuggestions((prev) => [...prev, ...data.items]);
                }
                setCurrentPage(page);
                setTotalPages(data.totalPages || 1);
                setCurrentKeyword(keyword);
                setShowLocationDropdown(true);
            } else {
                if (page === 1) {
                    setLocationSuggestions([]);
                }
                setShowLocationDropdown(true);
            }
        } catch (err) {
            console.error("Location suggestions error:", err);
            if (page === 1) {
                setLocationSuggestions([]);
            }
            setShowLocationDropdown(true);
        } finally {
            setIsLoadingLocations(false);
            setIsLoadingMore(false);
        }
    };

    // Handle input change with debouncing
    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value
            .replace(/[^A-Za-z0-9\s,]/g, "")
            .replace(/^,+/, "");

        // Clear selected location when user types
        if (selectedLocation) {
            setSelectedLocation(null);
        }

        onChange(input, null);

        if (debounceTimer) clearTimeout(debounceTimer);

        const trimmed = input.trim();
        if (trimmed.length >= 3) {
            // Reset pagination when user types new input
            setCurrentPage(1);
            setTotalPages(1);
            const timer = setTimeout(() => fetchLocationSuggestionsAPI(input, 1), 300);
            setDebounceTimer(timer);
        } else {
            setLocationSuggestions([]);
            setShowLocationDropdown(false);
            setCurrentPage(1);
            setTotalPages(1);
        }
    };

    // Prevent Enter key submission - user must select from suggestions
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            // Only close dropdown if suggestions are showing
            // Do not allow free-text submission
            if (showLocationDropdown) {
                setShowLocationDropdown(false);
            }
            // If no location selected, clear the input
            if (!selectedLocation) {
                onChange("", null);
            }
        }
    };

    // Handle location selection from dropdown
    const handleLocationSelect = (location: LocationSuggestion) => {
        const displayString = location.displayName || "";
        setSelectedLocation(location);
        onChange(displayString.trim(), location);
        setShowLocationDropdown(false);
        setLocationSuggestions([]);
    };

    // Handle dropdown scroll for infinite loading
    const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

        // Load more when scrolled near bottom (within 10px) and not already loading
        if (scrollBottom < 10 && !isLoadingMore && currentPage < totalPages) {
            fetchLocationSuggestionsAPI(currentKeyword, currentPage + 1);
        }
    };

    // Determine if clear icon should be shown
    const showClearIcon = value.trim().length > 0;

    return (
        <div className={`location-search-wrapper relative ${className}`}>
            <CustomInput
                value={value}
                placeholder={placeholder}
                trackingClassName={trackingClassName || MODAL_TRACKING.MODAL_TRACKING_LOCATION}
                testinputId="location-inputs"
                leftIcon={
                    <ImageWrapper
                        src="/location.svg"
                        alt="location icon"
                        width={18}
                        height={18}
                        cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                    />
                }
                rightIcon={
                    isLoadingLocations ? (
                        <div className="animate-spin h-4 w-4 border-2 border-[#008C8B] border-t-transparent rounded-full" />
                    ) : showClearIcon ? (
                        <button
                            type="button"
                            onClick={clearInput}
                            className="p-1 bg-gray-400 rounded-full hover:bg-gray-500 transition-colors cursor-pointer"
                            aria-label="Clear"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    ) : null
                }
                onChange={handleLocationChange}
                onKeyDown={handleKeyDown}
                error={error}
                labelClassName="!mb-1"
            />

            {showLocationDropdown && (
                <div 
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    onScroll={handleDropdownScroll}
                >
                    {locationSuggestions.length > 0 ? (
                        <>
                            {locationSuggestions.map((loc, idx) => (
                                <button
                                    key={loc.id || idx}
                                    type="button"
                                    onClick={() => handleLocationSelect(loc)}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors text-sm"
                                >
                                    {loc.displayName}
                                </button>
                            ))}
                            {isLoadingMore && (
                                <div className="flex justify-center items-center p-3">
                                    <div className="animate-spin h-5 w-5 border-2 border-[#008C8B] border-t-transparent rounded-full" />
                                    <span className="ml-2 text-sm text-gray-600">Loading more...</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="p-4 text-center">
                            <p className="text-sm text-gray-600 font-medium">No results found</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Try searching by city, district or pincode
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationMasterSearchInput;